const ALIGNMENT_CLASSES: Record<string, string> = {
	left: 'nha-align-left',
	center: 'nha-align-center',
	right: 'nha-align-right',
	justify: 'nha-align-justify'
};

// Tags the editor emits or a sanitized paste keeps, written the way a browser writes them (only
// `name="value"` attributes). An allowlist rather than "any tag", so plain text such as `Room <TBD>`,
// `<Ctrl>+<S>` or `<b and c>` is not mistaken for markup; the one-letter <s> only counts as a closing tag.
const HTML_TAG =
	/<\/?(a|b|i|u|strike|em|strong|p|br|div|span|ol|ul|li|dl|dt|dd|h[1-6]|blockquote|pre|code|kbd|mark|small|big|cite|q|del|ins|font|sub|sup|hr|img|table|thead|tbody|tr|td|th)(\s+[a-z][a-z0-9-]*=("[^"]*"|'[^']*'|[^\s>]+))*\s*\/?>|<\/s>/i;
// A rich note typed on one line is stored as a bare text node, so its only trace of HTML is entity encoding.
const HTML_ENTITY = /&(amp|lt|gt|quot|nbsp|#\d+|#x[\da-f]+);/i;
// A scheme is letters followed by a colon, unless the colon introduces a port (`host:8443/page`, `localhost:4200`).
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:(?!\d+(?:[/?#]|$))/i;
const EMAIL_ADDRESS = /^[^\s@/:]+@[^\s@/:]+\.[^\s@/:]+$/;
const ALLOWED_LINK_PROTOCOLS = ['https:', 'mailto:'];
const INVISIBLE_CHARS = /[\u200B-\u200D\uFEFF]/g;

/**
 * Whether a stored value is the rich editor's HTML, as opposed to plain text whose line breaks are
 * newlines and whose `<`, `&` and spaces are literal.
 */
export function isRichTextHtml(value: string | null | undefined): boolean {
	const text = value ?? '';
	return HTML_TAG.test(text) || HTML_ENTITY.test(text);
}

/**
 * Escapes a plain-text value into HTML the rich editor can hold without changing what it shows:
 * newlines become `<br>`, and runs of spaces or indentation keep their width.
 */
export function plainTextToHtml(text: string | null | undefined): string {
	return escapeHtml(text ?? '')
		.replace(/\t/g, '\u00A0\u00A0\u00A0\u00A0')
		.replace(/ (?= )|^ /gm, '\u00A0')
		.replace(/\r\n?|\n/g, '<br>')
		.replace(/\u00A0/g, '&nbsp;');
}

export function escapeHtml(text: string): string {
	return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** The characters a reader sees, which is what a length limit should count rather than the markup. */
export function visibleTextLength(html: string | null | undefined): number {
	if (!html) {
		return 0;
	}
	const text = new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
	return text.replace(INVISIBLE_CHARS, '').length;
}

/**
 * Returns the link to store in canonical form, or null when the entry is not an https: or mailto:
 * link. A bare address such as `nhaschools.com/page` or `intranet.local:8443/page` is treated as
 * https, and a bare email address as mailto.
 *
 * A link may also start with one of `linkPrefixes` (e.g. `<<schoolurl>>/admissions`), a placeholder
 * the consuming app's server swaps for a full https URL before the content is shown.
 */
export function normalizeLinkUrl(raw: string | null | undefined, linkPrefixes: readonly string[] = []): string | null {
	const trimmed = (raw ?? '').trim();
	if (!trimmed || /\s/.test(trimmed)) {
		return null;
	}
	const token = linkPrefixes.find((t) => trimmed.toLowerCase().startsWith(t.toLowerCase()));
	if (token) {
		return normalizeTokenLink(token, trimmed.slice(token.length));
	}
	let candidate = trimmed;
	if (!HAS_SCHEME.test(trimmed)) {
		candidate = EMAIL_ADDRESS.test(trimmed) ? `mailto:${trimmed}` : `https://${trimmed}`;
	}
	try {
		const url = new URL(candidate);
		if (!ALLOWED_LINK_PROTOCOLS.includes(url.protocol) || url.username || url.password) {
			return null;
		}
		const hasTarget = url.protocol === 'mailto:' ? url.pathname.length > 0 : url.hostname.length > 0;
		// The parsed form, so a typo such as `https:example.com` is not stored as a relative link.
		return hasTarget ? url.href : null;
	} catch {
		return null;
	}
}

/** `rest` is what follows the token: nothing, or a path, query or fragment on the token's URL. */
function normalizeTokenLink(token: string, rest: string): string | null {
	if (rest && !/^[/?#]/.test(rest)) {
		return null;
	}
	try {
		// Resolved against a stand-in origin only to canonicalize the path; the token stays as written.
		const url = new URL(rest || '/', 'https://token.invalid');
		if (url.origin !== 'https://token.invalid') {
			return null;
		}
		// A query or fragment straight after the token stays there, rather than gaining the root path's `/`.
		return token + (rest.startsWith('/') ? url.pathname : '') + url.search + url.hash;
	} catch {
		return null;
	}
}

/**
 * Rewrites the alignment the browser's editing commands wrote as inline styles (Chrome) or
 * `align` attributes (Firefox) into classes, which survive Angular's HTML sanitizer.
 */
export function alignmentToClasses(root: HTMLElement): void {
	root.querySelectorAll<HTMLElement>('[style*="text-align"], [align]').forEach((el) => {
		const alignment = (el.style.textAlign || el.getAttribute('align') || '').toLowerCase();
		el.style.removeProperty('text-align');
		if (!el.getAttribute('style')) {
			el.removeAttribute('style');
		}
		el.removeAttribute('align');
		Object.values(ALIGNMENT_CLASSES).forEach((cls) => el.classList.remove(cls));
		if (ALIGNMENT_CLASSES[alignment] && alignment !== 'left') {
			el.classList.add(ALIGNMENT_CLASSES[alignment]);
		}
		if (!el.classList.length) {
			el.removeAttribute('class');
		}
	});
}

/**
 * An editor that has been cleared still holds `<br>`, `<div><br></div>` or a zero-width space inside
 * an emptied inline tag; all of that counts as empty.
 */
export function isSurfaceEmpty(root: HTMLElement): boolean {
	return root.textContent.replace(INVISIBLE_CHARS, '').trim().length === 0 && !root.querySelector('img, hr, table');
}

/**
 * Whether the placeholder should show. An empty list still draws its marker, so it is empty as a
 * value but not blank on screen; drawing the placeholder over it would overlap the marker.
 */
export function isSurfaceBlank(root: HTMLElement): boolean {
	return isSurfaceEmpty(root) && !root.querySelector('ol, ul');
}

/**
 * Strips the wrapper Google Docs puts around copied text, which declares `font-weight: normal` on a
 * `<b>` and would read as bold once the sanitizer drops the style.
 */
export function stripPasteWrappers(html: string): string {
	return html.replace(/<b\s+style="font-weight:\s*normal;?"\s+id="docs-internal-guid-[^"]*">([\s\S]*)<\/b>/i, '$1');
}
